import {
  type ColScaffold,
  type RowScaffold,
  Scaffold,
  type ScaffoldDropZone,
} from "../../scaffold";
import { useArsContext } from "../contexts";
import { ArsDraggableSidebar } from "../sidebar/ArsDraggableSidebar";
import { ArsTableCellData } from "../table";
import { ArsListingAddColumnButton } from "./ArsListingAddColumnButton";
import { ArsListingAddRowButton } from "./ArsListingAddRowButton";
import { ArsListingCellEditorProps } from "./ArsListingCell";
import { ArsListingColumnProperties } from "./ArsListingColumnProperties";
import { ArsListingContextMenuProvider } from "./ArsListingContextMenuProvider";
import { useArsListing } from "./ArsListingContextProvider";
import { arsListingZoneColorResolver, buildApplyDroppedColumnsAction } from "./functions";
import { ARSL_SET_COLUMNS, ARSL_SET_ROW_HEIGHT, HEADER_ROW_ID } from "./interface";

/**
 * Renders ArsListing content inside ArsListingContextProvider.
 * Wrapper to handle the updates from Scaffold
 *
 * @returns ArsListing table and draggable variable sidebar
 */
export const ArsListingContent = <T,>(): React.ReactNode => {
  const arsContext = useArsContext();
  const { state, dispatch } = useArsListing<T>();
  const scaffoldModel = state?.scaffoldModel;
  if (!state || !scaffoldModel) return null;

  const arsListingBody = state.bodyData;
  const arsListingColumns = arsListingBody.arsListingColumns;
  const disabled = state.settings.disabled;
  const id = state.settings.id;
  const justification = state.settings.justification;

  /**
   * Handles variable drops on scaffold cells.
   *
   * @param event - Drop event
   * @param rowId - Target scaffold row id
   * @param colId - Target scaffold column id
   * @param zone - Classified drop zone within the target cell
   */
  const handleScaffoldDrop = (
    event: React.DragEvent<HTMLDivElement>,
    rowId: string,
    colId: string,
    zone: ScaffoldDropZone,
  ): void => {
    /* istanbul ignore else */
    if (!disabled && rowId === HEADER_ROW_ID) {
      event.preventDefault();

      const jsonPayload = event.dataTransfer.getData("application/json");

      const action = buildApplyDroppedColumnsAction(
        jsonPayload,
        colId,
        zone,
        state.settings.editorProps.textToT,
      );

      /* istanbul ignore else */
      if (action !== null) {
        dispatch(action);
      }
    }
  };

  /**
   * Handles column width changes from user resize operations.
   *
   * @param colId - Column id being resized
   * @param col - Updated column definition with new width
   */
  const handleChangeCol = (colId: string, col: ColScaffold<ArsTableCellData<T>>): void => {
    if (!disabled) {
      const updatedColumns = arsListingColumns.map((column) =>
        column.id === colId
          ? {
              ...column,
              columnWidth: col.width,
            }
          : column,
      );
      dispatch({
        operation: ARSL_SET_COLUMNS,
        columns: updatedColumns,
      });
    }
  };

  /**
   * Handles row height changes from user resize operations.
   *
   * @param rowId - Row id being resized
   * @param row - Updated row definition with new height
   */
  const handleChangeRow = (rowId: string, row: RowScaffold<ArsTableCellData<T>>): void => {
    if (!disabled) {
      if (rowId === HEADER_ROW_ID) {
        dispatch({
          operation: ARSL_SET_COLUMNS,
          columns: arsListingColumns.map((column) => ({ ...column, headerHeight: row.height })),
        });
      } else {
        const bodyRowIndex = arsListingBody.arsBodyRows.findIndex((r) => r.id === rowId);
        /* istanbul ignore else -- scaffold only passes rowIds built from arsBodyRows */
        if (bodyRowIndex !== -1) {
          dispatch({
            operation: ARSL_SET_ROW_HEIGHT,
            rowId,
            rowHeight: row.height,
          });
        }
      }
    }
  };

  return (
    <>
      <div
        className="d-flex flex-row"
        style={{ width: "100%", minWidth: 0, minHeight: "500px" }}
      >
        <div style={{ width: "100%", minWidth: 0 }}>
          <div className="d-flex flex-row gap-1">
            <ArsListingContextMenuProvider selectedColumns={arsListingColumns}>
              <Scaffold<ArsTableCellData<T>>
                id={id}
                cellComponent={ArsListingCellEditorProps(state.settings.editorProps)}
                cellData={scaffoldModel.cellData}
                columns={scaffoldModel.columns}
                disabled={disabled}
                justification={justification}
                onChangeCol={handleChangeCol}
                onChangeRow={handleChangeRow}
                onDrop={
                  arsContext.state.currentSidebar.view === "variable"
                    ? handleScaffoldDrop
                    : undefined
                }
                rows={scaffoldModel.rows}
                style={state.settings.style}
                zoneColors={
                  arsContext.state.currentSidebar.view === "variable"
                    ? arsListingZoneColorResolver
                    : () => ({ color: null })
                }
              />
            </ArsListingContextMenuProvider>
            <ArsListingAddColumnButton />
          </div>
          <div
            className="ars-listing-footer"
            style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
          >
            <ArsListingAddRowButton />
          </div>
        </div>
      </div>
      <ArsDraggableSidebar
        showGroupingFactors={true}
        showOperations={false}
        showVariables
        style={state.settings.sidebarStyle}
      />
      <ArsListingColumnProperties />
    </>
  );
};

ArsListingContent.displayName = "ArsListingContent";
